/**
 * Charts for the Building Electrification Policy panel.
 * Uses existing data from buildingsBauPathway and buildingEmissionsTimeSeries (from policyTimeSeriesService / calculatePolicyImpact).
 */
import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { BuildingsBauPathway, BuildingsBauScenario } from '../../services/buildingsBauPathways';
import {
  getPathwayRngShare,
  getPathwayTherms,
  getPathwayKwh,
  NG_EMISSION_FACTOR_KG_PER_THERM,
} from '../../services/buildingsBauPathways';
import type { BuildingEmissionsTimeSeries } from './PolicyImpactTool';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const COLORS = {
  electrified: 'rgb(34, 197, 94)',   // green
  fossil: 'rgb(234, 88, 12)',        // orange
  electricity: 'rgb(59, 130, 246)', // blue
  naturalGas: 'rgb(234, 88, 12)',   // orange
  bau: 'rgb(107, 114, 128)',         // gray
  policy: 'rgb(34, 197, 94)',        // green
  rng: 'rgb(168, 85, 247)',         // purple
};

const mtFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, notation: 'standard' });
const pctFormatter = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
const numFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, notation: 'standard' });

const CHART_YEAR_MIN = 2023;
const CHART_YEAR_MAX = 2045;

const commonOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' as const },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
};

// ---------------------------------------------------------------------------
// 1. Stock Evolution Chart
// ---------------------------------------------------------------------------
export interface BuildingsStockChartProps {
  pathway: BuildingsBauPathway | null;
}

export const BuildingsStockChart: React.FC<BuildingsStockChartProps> = ({ pathway }) => {
  const { labels, electrifiedPct, fossilPct } = useMemo(() => {
    if (!pathway?.years?.length || !pathway.stockElectrifiedByYear?.length || !pathway.stockFossilByYear?.length) {
      return { labels: [] as string[], electrifiedPct: [] as number[], fossilPct: [] as number[] };
    }
    const years = pathway.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const electrifiedPct = years.map((y) => {
      const i = pathway.years.indexOf(y);
      return (pathway.stockElectrifiedByYear![i] ?? 0) * 100;
    });
    const fossilPct = years.map((y) => {
      const i = pathway.years.indexOf(y);
      return (pathway.stockFossilByYear![i] ?? 0) * 100;
    });
    return {
      labels: years.map(String),
      electrifiedPct,
      fossilPct,
    };
  }, [pathway]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Electrified Share',
          data: electrifiedPct,
          borderColor: COLORS.electrified,
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Fossil Share',
          data: fossilPct,
          borderColor: COLORS.fossil,
          backgroundColor: 'rgba(234, 88, 12, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
      ],
    }),
    [labels, electrifiedPct, fossilPct]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          max: 100,
          title: { display: true, text: 'Share (%)' },
          ticks: { callback: (v) => (typeof v === 'number' ? `${v}%` : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? pctFormatter.format(Number(ctx.parsed.y) / 100) : '—'}`,
          },
        },
      },
    }),
    []
  );

  if (!pathway || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Electrification Over Time (BAU)</h5>
        <p className="building-electrification-chart-empty">Pathway data not available for this location.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Electrification Over Time (BAU)</h5>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Energy Transition Chart (dual-axis: therms + kWh)
// ---------------------------------------------------------------------------
export interface BuildingsEnergyChartProps {
  pathway: BuildingsBauPathway | null;
}

export const BuildingsEnergyChart: React.FC<BuildingsEnergyChartProps> = ({ pathway }) => {
  const { labels, therms, kwh } = useMemo(() => {
    if (!pathway?.years?.length || !pathway.thermsByYear?.length || !pathway.kwhByYear?.length) {
      return { labels: [] as string[], therms: [] as number[], kwh: [] as number[] };
    }
    const years = pathway.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const therms = years.map((y) => pathway.thermsByYear[pathway.years.indexOf(y)] ?? 0);
    const kwh = years.map((y) => pathway.kwhByYear[pathway.years.indexOf(y)] ?? 0);
    return { labels: years.map(String), therms, kwh };
  }, [pathway]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Natural Gas (therms)',
          data: therms,
          borderColor: COLORS.naturalGas,
          backgroundColor: 'rgba(234, 88, 12, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
          yAxisID: 'yTherms',
        },
        {
          label: 'Electricity (kWh)',
          data: kwh,
          borderColor: COLORS.electricity,
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
          yAxisID: 'yKwh',
        },
      ],
    }),
    [labels, therms, kwh]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        yTherms: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Natural Gas (therms)' },
          ticks: { callback: (v) => (typeof v === 'number' ? numFormatter.format(v) : v) },
        },
        yKwh: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'Electricity (kWh)' },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v) => (typeof v === 'number' ? numFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              const label = ctx.dataset.label || '';
              if (label.includes('therms')) return `${label}: ${typeof v === 'number' ? numFormatter.format(v) : '—'}`;
              return `${label}: ${typeof v === 'number' ? numFormatter.format(v) : '—'}`;
            },
          },
        },
      },
    }),
    []
  );

  if (!pathway || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Energy Transition (Therms → Electricity)</h5>
        <p className="building-electrification-chart-empty">Pathway data not available.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Energy Transition (Therms → Electricity)</h5>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. Emissions Comparison: BAU vs Policy
// ---------------------------------------------------------------------------
export interface BuildingsEmissionsChartProps {
  buildingEmissionsTimeSeries: BuildingEmissionsTimeSeries | null;
}

export const BuildingsEmissionsChart: React.FC<BuildingsEmissionsChartProps> = ({ buildingEmissionsTimeSeries }) => {
  const { labels, bauTotal, policyTotal } = useMemo(() => {
    if (!buildingEmissionsTimeSeries?.baseline?.length || !buildingEmissionsTimeSeries?.policy?.length) {
      return { labels: [] as string[], bauTotal: [] as number[], policyTotal: [] as number[] };
    }
    const b = buildingEmissionsTimeSeries.baseline;
    const p = buildingEmissionsTimeSeries.policy;
    const labels = b.map((d) => String(d.year));
    const bauTotal = b.map((d) => d.naturalGas + d.electricity);
    const policyTotal = p.map((d) => d.naturalGas + d.electricity);
    return { labels, bauTotal, policyTotal };
  }, [buildingEmissionsTimeSeries]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'BAU (buildings)',
          data: bauTotal,
          borderColor: COLORS.bau,
          backgroundColor: 'rgba(107, 114, 128, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Policy (buildings)',
          data: policyTotal,
          borderColor: COLORS.policy,
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
      ],
    }),
    [labels, bauTotal, policyTotal]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          title: { display: true, text: 'MT CO₂e' },
          ticks: { callback: (v) => (typeof v === 'number' ? mtFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? mtFormatter.format(Number(ctx.parsed.y)) : '—'} MT CO₂e`,
          },
        },
      },
    }),
    []
  );

  if (!buildingEmissionsTimeSeries || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Emissions: BAU vs Policy</h5>
        <p className="building-electrification-chart-empty">Emissions time series not loaded yet. Change a lever or wait for the chart above to load.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Emissions: BAU vs Policy</h5>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 4. Emissions Stack: NG + Electricity (BAU)
// ---------------------------------------------------------------------------
export const BuildingsEmissionsStackChart: React.FC<BuildingsEmissionsChartProps> = ({ buildingEmissionsTimeSeries }) => {
  const { labels, naturalGas, electricity } = useMemo(() => {
    if (!buildingEmissionsTimeSeries?.baseline?.length) {
      return { labels: [] as string[], naturalGas: [] as number[], electricity: [] as number[] };
    }
    const b = buildingEmissionsTimeSeries.baseline;
    const labels = b.map((d) => String(d.year));
    const naturalGas = b.map((d) => d.naturalGas);
    const electricity = b.map((d) => d.electricity);
    return { labels, naturalGas, electricity };
  }, [buildingEmissionsTimeSeries]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Natural Gas',
          data: naturalGas,
          borderColor: COLORS.naturalGas,
          backgroundColor: 'rgba(234, 88, 12, 0.6)',
          tension: 0.2,
          fill: true,
          stack: 'stack1',
          pointRadius: 1,
        },
        {
          label: 'Electricity',
          data: electricity,
          borderColor: COLORS.electricity,
          backgroundColor: 'rgba(59, 130, 246, 0.6)',
          tension: 0.2,
          fill: true,
          stack: 'stack1',
          pointRadius: 1,
        },
      ],
    }),
    [labels, naturalGas, electricity]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          stacked: true,
          min: 0,
          title: { display: true, text: 'MT CO₂e' },
          ticks: { callback: (v) => (typeof v === 'number' ? mtFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? mtFormatter.format(Number(ctx.parsed.y)) : '—'} MT CO₂e`,
          },
        },
      },
    }),
    []
  );

  if (!buildingEmissionsTimeSeries || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Building Emissions by Source (BAU)</h5>
        <p className="building-electrification-chart-empty">Emissions time series not loaded yet.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Building Emissions by Source (BAU)</h5>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 5. RNG Impact: RNG share over time (ramp when override set)
// ---------------------------------------------------------------------------
export interface RngImpactChartProps {
  pathway: BuildingsBauPathway | null;
  /** When set, show ramp from pathway 2023 to this 2045 share (%). */
  rngOverridePercent?: number;
}

export const RngImpactChart: React.FC<RngImpactChartProps> = ({ pathway, rngOverridePercent }) => {
  const { labels, rngPct } = useMemo(() => {
    if (!pathway?.years?.length || !pathway.rngShareByYear?.length) {
      return { labels: [] as string[], rngPct: [] as number[] };
    }
    const years = pathway.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const baseYear = 2023;
    const targetYear = 2045;
    const denom = targetYear - baseYear;
    const baseShare = getPathwayRngShare(pathway, baseYear);
    const targetShare = rngOverridePercent != null ? rngOverridePercent / 100 : getPathwayRngShare(pathway, targetYear);

    const rngPct = years.map((y) => {
      if (rngOverridePercent != null) {
        const t = y <= baseYear ? 0 : y >= targetYear ? 1 : (y - baseYear) / denom;
        return (baseShare + (targetShare - baseShare) * t) * 100;
      }
      return getPathwayRngShare(pathway, y) * 100;
    });
    return { labels: years.map(String), rngPct };
  }, [pathway, rngOverridePercent]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'RNG Share (%)',
          data: rngPct,
          borderColor: COLORS.rng,
          backgroundColor: 'rgba(168, 85, 247, 0.2)',
          tension: 0.2,
          fill: true,
          pointRadius: 2,
        },
      ],
    }),
    [labels, rngPct]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          max: 100,
          title: { display: true, text: 'RNG Share (%)' },
          ticks: { callback: (v) => (typeof v === 'number' ? `${v}%` : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `RNG: ${ctx.parsed.y != null ? Number(ctx.parsed.y).toFixed(1) : '—'}%`,
          },
        },
      },
    }),
    []
  );

  if (!pathway || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Impact of Renewable Natural Gas</h5>
        <p className="building-electrification-chart-empty">Pathway data not available.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Impact of Renewable Natural Gas</h5>
      <p className="building-electrification-chart-note">
        {rngOverridePercent != null
          ? `Ramp to ${rngOverridePercent}% by 2045 (panel override).`
          : 'RNG share from BAU pathway.'}
      </p>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 6. BAU Scenario Comparison: Conservative vs Current Policy vs Accelerated
// ---------------------------------------------------------------------------
export interface BuildingsBauComparisonChartProps {
  /** Pathways for all three BAU scenarios (from getBuildingsBauPathway). */
  pathwaysByScenario: Record<BuildingsBauScenario, BuildingsBauPathway> | null;
  /** 2023 electricity carbon intensity (g CO₂e/kWh) for grid ramp. */
  baseGPerKwh: number;
  /** Year grid reaches zero emissions; used for g/kWh ramp. */
  cleanElectricityYear: number;
}

/** Compute BAU-only building emissions (MT CO₂e) for one pathway and one year. Reuses same formula as policyTimeSeriesService. */
function bauBuildingEmissionsForYear(
  pathway: BuildingsBauPathway,
  year: number,
  baseGPerKwh: number,
  cleanElectricityYear: number
): number {
  const therms = getPathwayTherms(pathway, year);
  const kwh = getPathwayKwh(pathway, year);
  const rngShare = getPathwayRngShare(pathway, year);
  const denom = Math.max(1, cleanElectricityYear - 2023);
  const t = Math.max(0, Math.min(1, (year - 2023) / denom));
  const gPerKwh = baseGPerKwh * (1 - t);
  const ngMT = (therms * (1 - rngShare) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
  const elecMT = (kwh * gPerKwh) / 1_000_000;
  return ngMT + elecMT;
}

export const BuildingsBauComparisonChart: React.FC<BuildingsBauComparisonChartProps> = ({
  pathwaysByScenario,
  baseGPerKwh,
  cleanElectricityYear,
}) => {
  const { labels, conservativeMT, currentPolicyMT, acceleratedMT } = useMemo(() => {
    if (!pathwaysByScenario) {
      return { labels: [] as string[], conservativeMT: [] as number[], currentPolicyMT: [] as number[], acceleratedMT: [] as number[] };
    }
    const years: number[] = [];
    for (let y = CHART_YEAR_MIN; y <= CHART_YEAR_MAX; y++) years.push(y);
    const conservativeMT = years.map((y) =>
      bauBuildingEmissionsForYear(pathwaysByScenario.conservative, y, baseGPerKwh, cleanElectricityYear)
    );
    const currentPolicyMT = years.map((y) =>
      bauBuildingEmissionsForYear(pathwaysByScenario.current_policy, y, baseGPerKwh, cleanElectricityYear)
    );
    const acceleratedMT = years.map((y) =>
      bauBuildingEmissionsForYear(pathwaysByScenario.accelerated, y, baseGPerKwh, cleanElectricityYear)
    );
    return {
      labels: years.map(String),
      conservativeMT,
      currentPolicyMT,
      acceleratedMT,
    };
  }, [pathwaysByScenario, baseGPerKwh, cleanElectricityYear]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Conservative BAU',
          data: conservativeMT,
          borderColor: 'rgb(148, 163, 184)',
          backgroundColor: 'rgba(148, 163, 184, 0.1)',
          borderDash: [8, 4],
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Current Policy BAU',
          data: currentPolicyMT,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          tension: 0.2,
          fill: false,
          pointRadius: 2,
        },
        {
          label: 'Accelerated BAU',
          data: acceleratedMT,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          borderWidth: 3,
          tension: 0.2,
          fill: false,
          pointRadius: 3,
        },
      ],
    }),
    [labels, conservativeMT, currentPolicyMT, acceleratedMT]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          min: 0,
          title: { display: true, text: 'MT CO₂e (buildings only)' },
          ticks: { callback: (v) => (typeof v === 'number' ? mtFormatter.format(v) : v) },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        tooltip: {
          ...commonOptions.plugins?.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y != null ? mtFormatter.format(Number(ctx.parsed.y)) : '—'} MT CO₂e`,
          },
        },
      },
    }),
    []
  );

  if (!pathwaysByScenario || labels.length === 0) {
    return (
      <div className="building-electrification-chart-card">
        <h5>Buildings BAU Scenario Comparison (Emissions)</h5>
        <p className="building-electrification-chart-empty">Pathway data not available for this location.</p>
      </div>
    );
  }

  return (
    <div className="building-electrification-chart-card">
      <h5>Buildings BAU Scenario Comparison (Emissions)</h5>
      <p className="building-electrification-chart-note">
        BAU-only building emissions (no local electrification). Grid decarbonizes to zero by {cleanElectricityYear}.
      </p>
      <div className="building-electrification-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};
